import { Navigate, Outlet } from 'react-router';
import { useAuthClient } from '@/lib/authClient';
import { useDeviceConnection } from '@/hooks/use-device-connection';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';

/**
 * Guards routes that require an authenticated session and connected mobile
 * device. While admin approval is pending, `useMyOrganization` returns no
 * organization and SWR rechecks membership every three seconds. Once the API
 * returns the approved organization, polling stops and the guard redirects the
 * user into the application.
 */
export function RequireFullAuthWithMobile() {
  const {
    data: sessionData,
    isPending: sessionPending,
    error: sessionError,
  } = useAuthClient().session;
  const { isChecking, isConnected } = useDeviceConnection();
  const { organization, isLoading } = useMyOrganization({
    // Keep org fetch paused until mobile status confirms connected.
    enabled: isConnected,
    // Poll while membership is pending; stop as soon as approval returns an org.
    refreshInterval: (latestOrganization) => (latestOrganization ? 0 : 3_000),
  });

  if (sessionPending) return null;
  // Case: session not found, redirect to signin
  if (!sessionData || sessionError) return <Navigate to="/signin" replace />;
  if (isChecking) return null;

  if (!isConnected) return <Navigate to="/register-device" replace />;
  if (isLoading) return null;
  if (organization) return <Navigate to="/" replace />;
  return <Outlet />;
}
